/**
 * Dependency imports
 */
import React from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import { Box, BoxProps, Container, Group, Text, Title } from '@mantine/core';

/**
 * Local imports
 */
import { useResponsive } from '@/hooks/use-responsive';

/**
 * Types
 */
export interface CalloutContent {
  title: React.ReactNode;
  children: React.ReactNode;
}

export interface CalloutStyles {
  background?: string;
  padding?: number;
  containerSize?: string;
  gap?: {
    xs?: number;
    lg?: number;
    xl?: number;
  };
  direction?: {
    xs?: 'column' | 'row';
    lg?: 'column' | 'row';
  };
  titleStyles?: {
    size?: number;
    weight?: number;
    flex?: number;
  };
  textStyles?: {
    size?: number;
    weight?: number;
    flex?: number;
  };
}

export interface CalloutProps extends CalloutContent, Partial<CalloutStyles> { }
export type CalloutCombinedProps = CalloutProps & BoxProps;

/**
 * Root
 */
const Root = styled(Box, {
  shouldForwardProp: (prop: PropertyKey) =>
    !['background', 'padding', 'containerSize', 'gap', 'direction', 'titleStyles', 'textStyles']
      .includes(prop.toString()),
})<Partial<CalloutCombinedProps>>((props) => css``);

/**
 * Default styles
 */
const defaultStyles: CalloutStyles = {
  background: 'gray.0',
  padding: 100,
  containerSize: 'xl',
  gap: {
    xs: 25,
    lg: 50,
    xl: 100,
  },
  direction: {
    xs: 'column',
    lg: 'row',
  },
  titleStyles: {
    size: 42,
    weight: 500,
    flex: 5,
  },
  textStyles: {
    size: 21,
    weight: 400,
    flex: 4,
  },
};

/**
 * Callout Component
 */
const Callout: React.FC<CalloutCombinedProps> = ({
  title,
  children,
  background = defaultStyles.background,
  padding = defaultStyles.padding,
  containerSize = defaultStyles.containerSize,
  gap = defaultStyles.gap,
  direction = defaultStyles.direction,
  titleStyles = defaultStyles.titleStyles,
  textStyles = defaultStyles.textStyles,
  ...props
}) => {
  const { responsive } = useResponsive();

  return (
    <Root bg={background} py={padding} {...props}>
      <Container size={containerSize}>
        <Group
          wrap="nowrap"
          gap={responsive({
            xs: gap?.xs,
            lg: gap?.lg,
            xl: gap?.xl,
          })}
          style={{
            flexDirection: responsive({
              xs: direction?.xs,
              lg: direction?.lg,
            }),
          }}
        >
          {typeof title === 'string' ? (
            <Title
              size={titleStyles?.size}
              fw={titleStyles?.weight}
              style={{ flex: titleStyles?.flex }}
            >
              {title}
            </Title>
          ) : (
            title
          )}
          <Box style={{ flex: textStyles?.flex }}>
            {typeof children === 'string' ? (
              <Text
                fw={textStyles?.weight}
                fz={textStyles?.size}
              >
                {children}
              </Text>
            ) : (
              children
            )}
          </Box>
        </Group>
      </Container>
    </Root>
  );
};

export default Callout;
